import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  EigentumanteilMasterId,
  EigentumanteilMasterIdMeta,
  EigentumanteilVersionIdMeta,
  EigentumPersonSichtListEntryMeta,
  GbeigentumService,
  LoadEigentumanteilSichtPersonstammResponseMeta,
  PersonStammMasterId
} from '../../../../core/gb-api';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OrderByGrundstuecknummerPipe } from '../../../../shared/pipes/order/order-by-grundstuecknummer.pipe';
import { AppPermissionService } from '../../../../core/services/app-permission.service';
import { NavTarget } from '../../../../shared/components/link/link.component';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { Observable, Subscription } from 'rxjs';
import { MapResponse } from '../../../../core/maps-api';
import { FormatConfigService } from '../../../../core/services/format-config.service';
import { HistoryMode } from 'app/core/services/HistoryMode';
import { ListLoader } from '../../../../shared/components/list-loader/list-loader';
import { ListLoaderPaginationBuilder } from '../../../../shared/components/list-loader/list-loader-pagination';
import { map } from 'rxjs/operators';
import { PaginationLookupResult } from '../../../../shared/components/list-loader/pagination-lookup-result';
import { AbstractComponent } from '../../../../shared/components/abstract-component/abstract.component';
import { ListLoaderPreloaded } from '../../../../shared/components/list-loader/list-loader-preloaded';
import { ListLoaderWrapperComponent } from '../../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { KompaktPersongbComponent } from '../../../../shared/kompaktdarstellung/persongb/kompakt-persongb.component';
import { HistoryDirective } from '../../../../shared/directives/history.directive';
import { KompaktGrundstueckComponent } from '../../../../shared/kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { CapSpinnerSmallComponent } from '../../../../shared/components/cap-spinner-small/cap-spinner-small.component';

@Component({
  selector: 'cap-personstamm-eigentum',
  templateUrl: './personstamm-eigentum.component.html',
  styleUrls: ['./personstamm-eigentum.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    TranslatePipe,
    KompaktPersongbComponent,
    HistoryDirective,
    KompaktGrundstueckComponent,
    CapSpinnerSmallComponent,
    OrderByGrundstuecknummerPipe
  ]
})
export class PersonstammEigentumComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  personStammMasterId: PersonStammMasterId;

  @Input()
  mitHist = true;

  listLoader: ListLoader<ViewModelEigentumPersonListEntry>;

  response: LoadEigentumanteilSichtPersonstammResponseMeta;
  viewModelEigentum: Array<ViewModelEigentumPersonListEntry>;
  pagedViewModelEigentum: Array<ViewModelEigentumPersonListEntry>;

  subscription: Subscription = new Subscription();
  hasEigentumPermission: boolean;
  navTargets = NavTarget;
  preloadedIds: Array<EigentumanteilMasterId>;
  private readonly PAGE_SIZE = 30;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private appConfigService: AppConfigService,
    protected appformatConfigService: FormatConfigService,
    public translate: TranslateService,
    private eigentuemerService: GbeigentumService,
    private orderByGrundstuecknr: OrderByGrundstuecknummerPipe,
    private permissionService: AppPermissionService,
    private timepointService: TimepointService
  ) {
    super();
    keysToTranslate<string>(['Eigentumsform', 'Plan', 'Auszug', 'Totalflaeche', 'AnteilEigentum']);
    this.hasEigentumPermission = this.permissionService.hasPermission('ANZEIGE_EIGENTUM');
  }

  ngOnInit(): void {
    this.viewModelEigentum = [];
    this.subscription.add(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        if (this.viewModelEigentum) {
          if (!this.hasEigentumPermission || !this.personStammMasterId) {
            this.viewModelEigentum = new Array<ViewModelEigentumPersonListEntry>();
            this.pagedViewModelEigentum = new Array<ViewModelEigentumPersonListEntry>();
            this.listLoader = new ListLoaderPreloaded(15, 1, []);
            return;
          }
          const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
          this.eigentuemerService
            .loadEigentumanteilMasterIdsSichtPersonstamm(
              this.personStammMasterId.id,
              this.timepointService.timepointCtx.timepointIso,
              null,
              historisierung
            )
            .subscribe((ids) => {
              this.preloadedIds = ids;
              this.loadEigentum();
            });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private loadEigentum() {
    const observable = (offset, limit, sortColumn) => this.createObservable(offset, limit);

    this.listLoader = ListLoaderPaginationBuilder.builder<ViewModelEigentumPersonListEntry>()
      .setNoSortModel(true)
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .build();
    this.addLoader(this.listLoader);
  }

  private createObservable(offset: number, limit: number): Observable<any> {
    const historisierung = this.mitHist ? this.appConfigService.einstellungHistorischeDaten : HistoryMode.HistoryMode.OHNE_HIST;
    const batchedIds = this.preloadedIds.slice(offset, offset + limit).map((id) => id.id);

    return this.eigentuemerService
      .loadEigentumSichtPersonstamm(
        this.personStammMasterId.id,
        batchedIds,
        this.timepointService.timepointCtx.timepointIso,
        null,
        historisierung
      )
      .pipe(
        map((data) => {
          this.viewModelEigentum = (<Array<EigentumPersonSichtListEntryMeta>>(
            this.orderByGrundstuecknr.transform(data.entries, true, 'grundstueckGbListEntry')
          )).map((d) => new ViewModelEigentumPersonListEntry(d));
          return <PaginationLookupResult<EigentumPersonSichtListEntryMeta>>{
            result: this.viewModelEigentum,
            totalItems: this.preloadedIds.length
          };
        })
      );
  }
}

class ViewModelEigentumPersonListEntry {
  id: EigentumanteilMasterIdMeta;
  versionId: EigentumanteilVersionIdMeta;
  mapLinkItem: MapResponse;
  model: EigentumPersonSichtListEntryMeta;

  constructor(eigentumPersonSichtListEntryMeta: EigentumPersonSichtListEntryMeta) {
    this.model = eigentumPersonSichtListEntryMeta;
    this.mapLinkItem = <MapResponse>{ url: null };
  }
}
